import UpgradeState from 'components/account-features/UpgradeState';
import knownApps from 'constants/known-apps';
import type { Integration } from 'factories/apps';
import { useCreateHref } from 'hooks/useCreateHref';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openAppEditor } from 'redux/modules/apps.actions';
import {
  getHasDataExportIntegrationsSupport,
  getHasTicIntegrationsSupport,
} from 'redux/selectors/accounts';
import { getAppEditorId, getEnhancedApps } from 'redux/selectors/apps';
import { getIsUserAdmin } from 'redux/selectors/user';
import type { ThunkDispatch } from 'types/redux';
import { IntegrationsListAsCards } from '~domains/integrations/components/Integrations.ListAsCards';
import { categories, make, timelyApi, timelyWebhooks, zapier } from './constants';
import styles from './EcosystemRoute.module.scss';
import IntegrationRow from './IntegrationRow';

const IntegrationsList: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const integrations = useSelector(getEnhancedApps);
  const appEditorId = useSelector(getAppEditorId);
  const isAdmin = useSelector(getIsUserAdmin);

  const hasTicIntegrationsSupport = useSelector(getHasTicIntegrationsSupport);
  const hasDataExportIntegrationsSupport = useSelector(getHasDataExportIntegrationsSupport);

  // Hide legacy integrations when newer equivalents are available,
  // unless the customer is already connected to them
  const legacyIntegrationIds: string[] = ['jira', 'clickup', 'asana', 'asana_sync', 'monday'];
  const isReplacedByTic = (i: Integration) =>
    hasTicIntegrationsSupport && legacyIntegrationIds.includes(i.id) && !i.integrated;

  const visibleIntegrations = integrations.filter(i => !isReplacedByTic(i));

  const timeTrackingIntegrations = visibleIntegrations.filter(i =>
    knownApps.timeTrackingIntegrations.includes(i.id as any)
  );

  const syncIntegrations = visibleIntegrations.filter(i =>
    knownApps.syncIntegrations.includes(i.id as any)
  );

  const reportingIntegrations = integrations.filter(i =>
    knownApps.reportingIntegrations.includes(i.id as any)
  );

  const trackers = integrations.filter(i => knownApps.trackers.includes(i.id as any));

  const createHref = useCreateHref();

  const handleIntegrationClick = useCallback(
    (integration: Integration) => {
      const { appId, url, navLink } = integration;
      if (appId) {
        dispatch(openAppEditor(appId));
      } else if (url) {
        window.open(url, '_blank', 'noopener, noreferrer');
      } else if (navLink) {
        window.location.href = createHref(navLink);
      }
    },
    [createHref, dispatch]
  );

  return (
    <>
      <h3 className={styles.categoryHeader} id={categories[1].value}>
        {categories[1].label}
      </h3>
      <p>
        Capture all records of active work on any tool and any device. (On Chrome or Linux OS?
        Contact support at <a href="mailto:support@timely.com">support@timely.com</a> to track
        browser-only activities only.)
      </p>
      <ul>
        {trackers.map(integration => (
          <IntegrationRow
            key={integration.id}
            integration={integration}
            onClick={handleIntegrationClick}
            isEdited={appEditorId === integration.appId}
          />
        ))}
      </ul>
      <h3 className={styles.categoryHeader} id="tic-integrations">
        Next-Gen Sync Integrations
      </h3>
      <p>
        Explore next-gen integrations that sync projects, tasks, and other data to Timely, and for
        supported tools, send tracked time back.
      </p>
      {!hasTicIntegrationsSupport && (
        <UpgradeState inline messageKey="ticIntegrationsRequiresUpgrade" />
      )}
      <IntegrationsListAsCards />
      <h3 className={styles.categoryHeader} id={categories[2].value}>
        {categories[2].label}
      </h3>
      <p>
        Time tracking integrations capture detailed records of active work in your chosen app, as
        well as the time spent on each record. For example, you'll see how many minutes you've spent
        on each Microsoft Teams chat, document, or space. <strong>Available on all plans.</strong>
      </p>
      <ul>
        {timeTrackingIntegrations.map(integration => (
          <IntegrationRow
            key={integration.id}
            integration={integration}
            onClick={handleIntegrationClick}
            isEdited={appEditorId === integration.appId}
          />
        ))}
      </ul>
      {syncIntegrations.length > 0 && ( // TODO: We need to stop hiding these on BE
        <>
          <h3 className={styles.categoryHeader} id={categories[3].value}>
            {categories[3].label}
          </h3>
          <p>
            Sync integrations bring in, replicate, or send information between other tools and
            Timely.{' '}
            <strong>You will need a Premium or Unlimited plan to enable some of them.</strong>
          </p>
          <ul>
            {syncIntegrations.map(integration => (
              <IntegrationRow
                key={integration.id}
                integration={integration}
                onClick={handleIntegrationClick}
                isEdited={appEditorId === integration.appId}
              />
            ))}
          </ul>
        </>
      )}
      {hasDataExportIntegrationsSupport && reportingIntegrations.length > 0 && (
        <>
          <h3 className={styles.categoryHeader} id={categories[4].value}>
            {categories[4].label}
          </h3>
          <p>
            Export and analyze your time tracking data in external reporting tools.{' '}
            <strong>Setup requires workspace admin privileges.</strong>
          </p>
          <ul>
            {reportingIntegrations.map(integration => (
              <IntegrationRow
                key={integration.id}
                integration={integration}
                onClick={handleIntegrationClick}
                isEdited={appEditorId === integration.appId}
              />
            ))}
          </ul>
        </>
      )}

      <h3 className={styles.categoryHeader} id={categories[5].value}>
        {categories[5].label}
      </h3>
      <p>
        Looking for something a little more customised, and have the skills to craft it yourself?
        Talk to our Support Team about your requirements or start with the options below.
      </p>
      <ul>
        <li className={styles.subcategoryHeader}>Developer tools</li>
        <IntegrationRow integration={timelyApi} onClick={handleIntegrationClick} />
        {isAdmin && (
          <IntegrationRow integration={timelyWebhooks} onClick={handleIntegrationClick} />
        )}
        <li className={styles.subcategoryHeader}>Automations</li>
        <IntegrationRow integration={zapier} onClick={handleIntegrationClick} />
        <IntegrationRow integration={make} onClick={handleIntegrationClick} />
      </ul>
    </>
  );
};

export default IntegrationsList;
